import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Lineicons } from '@lineiconshq/react-lineicons';
import { XmarkStroke } from '@lineiconshq/free-icons';
import { useEffect, useMemo, useState } from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from '@/components/ui/dialog';
import { getDocumentsV2InitialValues, getDocumentsV2Schema } from './schema';
import { AritoFormV2, BottomActionBarV2 } from '@/components/custom/erp';
import type { BoPhan, KhachHang } from '@/types/schemas';
import { AritoIcon } from '@/components/custom/arito';
import { useDetailRows } from './hooks/useDetailRows';
import DetailTab from './DetailTab/index';
import BasicInfoTab from './BasicInfoTab';
import { useDialogStack } from '@/hooks';
import { cn } from '@/lib/utils';

export interface GetDocumentsV2Props {
  onClose: () => void;
  onSearch: (filters: any) => void;
  chungTuType: string;
  detailColumns?: any[];
}

export default function GetDocumentsV2({ onClose, onSearch, chungTuType, detailColumns }: GetDocumentsV2Props) {
  const [hoaDon, setHoaDon] = useState<any[]>([]);
  const [boPhan, setBoPhan] = useState<BoPhan | null>(null);
  const [customer, setCustomer] = useState<KhachHang | null>(null);
  const [selectedDetailRowIds, setSelectedDetailRowIds] = useState<string[]>([]);
  const [selectedDetailRowUuid, setSelectedDetailRowUuid] = useState<string | null>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const { contentZIndex, overlayZIndex } = useDialogStack(true);

  const detailRowsData = useMemo(
    () => hoaDon.flatMap(invoice => invoice?.chi_tiet || invoice?.chi_tiet_data || invoice?.chi_tiet_hoa_don || []),
    [hoaDon]
  );
  const detailRows = useDetailRows(detailRowsData);

  useEffect(() => {
    setSelectedDetailRowIds([]);
    setSelectedDetailRowUuid(null);
  }, [detailRows]);

  const handleSubmit = (data: any) => {
    const selectedRows = detailRows.filter(row => selectedDetailRowIds.includes(String(row.uuid || ''))) as any[];
    const dienGiai = hoaDon[0]?.dien_giai || selectedRows[0]?.dien_giai || '';

    onSearch({
      xprocess: data?.xprocess || '2',
      customer,
      selectedRows,
      dien_giai: dienGiai
    });

    onClose();
  };

  return (
    <Dialog
      open={true}
      onOpenChange={val => {
        if (!val) onClose();
      }}
    >
      <DialogPortal>
        <DialogOverlay className='fixed inset-0 bg-black/50 backdrop-blur-none' style={{ zIndex: overlayZIndex }} />
        <div className='fixed inset-0 flex items-center justify-center p-4' style={{ zIndex: contentZIndex }}>
          <DialogPrimitive.Content
            onEscapeKeyDown={e => e.preventDefault()}
            className={cn(
              'flex w-full flex-col overflow-hidden border border-[#cfd8e3] bg-white shadow-lg outline-none transition-all duration-200',
              isFullScreen ? 'fixed inset-0 h-full max-w-none' : 'h-[90vh] max-w-4xl'
            )}
          >
            {/* Header */}
            <div className='flex h-[var(--erp-dialog-header-height)] min-h-[var(--erp-dialog-header-height)] items-center justify-between border-b border-[#d1d5db] bg-[#f2f7fc] pl-3.5 pr-4'>
              <DialogTitle className='flex items-center text-[length:var(--erp-dialog-title-size)] font-bold text-black'>
                <AritoIcon icon={12} className='mx-2' />
                <span>Chi tiết chứng từ được chọn</span>
              </DialogTitle>
              <div className='flex items-center gap-1'>
                <button
                  type='button'
                  onClick={() => setIsFullScreen(prev => !prev)}
                  className='inline-flex size-8 items-center justify-center text-black/80 hover:text-black'
                  title={isFullScreen ? 'Thu nhỏ' : 'Phóng to'}
                >
                  {isFullScreen ? <Minimize2 className='size-[16px]' /> : <Maximize2 className='size-[16px]' />}
                </button>
                <button
                  type='button'
                  onClick={onClose}
                  className='inline-flex size-8 items-center justify-center text-black/80 hover:text-black'
                  title='Đóng'
                >
                  <Lineicons aria-hidden='true' icon={XmarkStroke} className='size-[18px]' />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className='min-h-0 min-w-0 flex-1 overflow-hidden'>
              <AritoFormV2
                mode='add'
                hasAritoActionBar={false}
                onSubmit={handleSubmit}
                className='flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden'
                schema={getDocumentsV2Schema}
                initialData={getDocumentsV2InitialValues}
                headerFields={
                  <BasicInfoTab
                    chungTuType={chungTuType}
                    hoaDon={hoaDon}
                    customer={customer}
                    setCustomer={setCustomer}
                    setHoaDon={setHoaDon}
                    boPhan={boPhan}
                    setBoPhan={setBoPhan}
                  />
                }
                tabs={[
                  {
                    id: 'detail',
                    label: 'Chi tiết',
                    component: (
                      <DetailTab
                        rows={detailRows}
                        detailColumns={detailColumns}
                        selectedRowUuid={selectedDetailRowUuid}
                        selectedRowIds={selectedDetailRowIds}
                        onRowClick={params => setSelectedDetailRowUuid(String(params.id))}
                        onRowSelectionChange={setSelectedDetailRowIds}
                      />
                    )
                  }
                ]}
                bottomBar={<BottomActionBarV2 onClose={onClose} mode='add' />}
              />
            </div>
          </DialogPrimitive.Content>
        </div>
      </DialogPortal>
    </Dialog>
  );
}
